import { z } from 'zod';

/**
 * One service that can be pointed at an ephemeral PR environment.
 *
 * `.passthrough()` and the nullish `label` follow the house style: the backend may add fields, and a
 * missing display name should degrade to the app label rather than fail the whole screen.
 */
export const PrEnvServiceSchema = z
  .object({
    key: z.string(),
    label: z.string().nullish(),
    app_label: z.string(),
    header: z.string().nullish(),
    host: z.string().nullish(),
    // support-gateway itself. Defaulted rather than nullish: an older gateway that does not send the
    // field has no self target either, so false is the right answer for it and not a guess.
    self: z.boolean().default(false),
  })
  .passthrough();

/**
 * The rules travel with the list. The client validates against `value_pattern` and `max_headers`
 * rather than re-declaring them, and `async_forwarding_supported` is what the dialog's "bulk actions
 * always run against stable" note is driven by.
 */
export const PrEnvTargetsResponseSchema = z
  .object({
    enabled: z.boolean().default(false),
    header_prefix: z.string().default('x-pr-env-'),
    value_pattern: z.string().nullish(),
    max_headers: z.number().nullish(),
    async_forwarding_supported: z.boolean().default(false),
    services: z.array(PrEnvServiceSchema).default([]),
  })
  .passthrough();

export type TPrEnvServicePayload = z.infer<typeof PrEnvServiceSchema>;
export type TPrEnvTargetsPayload = z.infer<typeof PrEnvTargetsResponseSchema>;

/** A service as the UI consumes it, with the label fallback already applied. */
export interface IPrEnvService {
  key: string;
  label: string;
  appLabel: string;
  /**
   * support-gateway itself. Routing it forks only the panel's API calls — its own Istio
   * VirtualService matches the header, and a document load cannot carry one, so the UI and its assets
   * still come from stable. The control says so, because otherwise a frontend-only PR looks broken.
   */
  self: boolean;
}

export interface IPrEnvTargets {
  enabled: boolean;
  maxHeaders: number | null;
  asyncForwardingSupported: boolean;
  services: IPrEnvService[];
}
